import type { ThemeConfig } from "antd";

// antd theme built from the design-system tokens (src/styles/ds, generated
// themes/light.css) — the same values the client app (AppInstalls) is drawn
// with. antd derives hover/active shades from these, so they have to be
// literal colours rather than var(--…); the token each one mirrors is named
// next to it.
const ds = {
  brand: "#4858a8", // --button-brand-primary-bg-default
  brandHover: "#616fb4", // --button-brand-primary-bg-hover
  brandSubtle: "#e9ecf7", // --blue-background-subtle
  brandSubtlest: "#f6f7fb", // --blue-background-subtlest
  textPrimary: "#202020", // --ui-text-primary-default
  textSecondary: "#393b42", // --ui-text-secondary-default
  textCaption: "#7c7e8a", // --ui-text-caption-default
  textTertiary: "#9698a3", // --ui-text-tertiary-default
  textPlaceholder: "#a8aab2", // --ui-text-placeholder-default
  borderPrimary: "#d7d9e0", // --ui-border-primary-default
  borderSecondary: "#e9eaf2", // --ui-border-secondary-default
  pageBg: "#f5f6fc", // --ui-page-bg-default
  cardBg: "#ffffff", // --ui-card-bg-default
  success: "#12b76a", // --success-background-strong
  warning: "#f79009", // --badge-warning-solid-bg-default
  error: "#f04438", // --error-background-strong
  errorHover: "#f25b50", // --button-destructive-primary-bg-hover
  font: '"Rubik", system-ui, sans-serif', // --ds-font-body
  shadowPopover: "0 12px 64px 0 rgba(17, 27, 53, 0.08)",
};

export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary: ds.brand,
    colorInfo: ds.brand,
    colorLink: ds.brand,
    colorLinkHover: ds.brandHover,
    colorSuccess: ds.success,
    colorWarning: ds.warning,
    colorError: ds.error,

    colorText: ds.textSecondary,
    colorTextHeading: ds.textPrimary,
    colorTextSecondary: ds.textCaption,
    colorTextTertiary: ds.textTertiary,
    colorTextDescription: ds.textTertiary,
    colorTextPlaceholder: ds.textPlaceholder,
    colorTextDisabled: ds.textTertiary,

    colorBorder: ds.borderPrimary,
    colorBorderSecondary: ds.borderSecondary,
    colorSplit: ds.borderSecondary,

    colorBgLayout: ds.pageBg,
    colorBgContainer: ds.cardBg,
    colorBgElevated: ds.cardBg,
    colorBgContainerDisabled: ds.pageBg,
    colorFillAlter: ds.pageBg,
    colorBgMask: "rgba(0, 0, 0, 0.45)",
    colorBgSpotlight: ds.brand, // --ui-tooltip-bg-default: tooltips are blue

    controlItemBgHover: ds.brandSubtlest,
    controlItemBgActive: ds.brandSubtle,
    controlItemBgActiveHover: ds.brandSubtle,
    controlOutline: "rgba(72, 88, 168, 0.1)",

    fontFamily: ds.font,
    fontSize: 14, // body/small 14/20
    lineHeight: 20 / 14,
    fontSizeHeading1: 28, // heading/large 28/36
    fontSizeHeading2: 20,
    fontSizeHeading3: 18, // heading/small 18/24
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,

    borderRadius: 8, // control/extraSmall (chips, inputs)
    borderRadiusXS: 4,
    borderRadiusSM: 8,
    borderRadiusLG: 16, // margin/medium (card)

    controlHeight: 32, // control/m
    controlHeightSM: 28, // control/xs
    controlHeightLG: 40,

    boxShadow: ds.shadowPopover,
    boxShadowSecondary: ds.shadowPopover,
    wireframe: false,
  },
  components: {
    Button: {
      borderRadius: 10, // control/medium (buttons)
      borderRadiusSM: 8,
      borderRadiusLG: 12,
      fontWeight: 500,
      paddingInline: 12, // button/padding/m
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
      defaultColor: ds.textSecondary,
      defaultBorderColor: ds.borderSecondary,
      defaultHoverBg: ds.brandSubtlest,
      defaultHoverColor: ds.brand,
      defaultHoverBorderColor: ds.brand,
      defaultActiveBg: ds.brandSubtle,
      colorErrorHover: ds.errorHover,
    },
    Input: {
      activeShadow: "0 0 0 2px rgba(72, 88, 168, 0.1)",
      paddingInline: 12,
    },
    InputNumber: {
      activeShadow: "0 0 0 2px rgba(72, 88, 168, 0.1)",
    },
    Select: {
      optionSelectedBg: ds.brandSubtle,
      optionActiveBg: ds.brandSubtlest,
      optionSelectedFontWeight: 500,
      optionSelectedColor: ds.brand,
    },
    DatePicker: {
      activeShadow: "0 0 0 2px rgba(72, 88, 168, 0.1)",
    },
    Table: {
      headerBg: ds.cardBg,
      headerColor: ds.textSecondary,
      headerSplitColor: "transparent",
      headerSortActiveBg: ds.brandSubtlest,
      headerSortHoverBg: ds.brandSubtlest,
      bodySortBg: "transparent",
      borderColor: ds.borderSecondary,
      rowHoverBg: ds.brandSubtlest,
      rowSelectedBg: ds.brandSubtle,
      rowSelectedHoverBg: ds.brandSubtle,
      footerBg: ds.cardBg,
      cellPaddingBlock: 12,
      cellPaddingInline: 16,
      cellPaddingBlockMD: 10,
      cellPaddingInlineMD: 12,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 8,
      headerBorderRadius: 12,
    },
    Tabs: {
      itemColor: ds.textTertiary,
      itemHoverColor: ds.brand,
      itemSelectedColor: ds.brand,
      itemActiveColor: ds.brand,
      inkBarColor: ds.brand,
      titleFontSize: 16, // body/medium
      horizontalItemGutter: 32,
      horizontalItemPadding: "8px 0 12px",
    },
    Modal: {
      borderRadiusLG: 20, // element/large
      titleFontSize: 18, // heading/small
      titleLineHeight: 24 / 18,
      titleColor: ds.textPrimary,
    },
    Drawer: {
      colorBgElevated: ds.cardBg,
    },
    Card: {
      borderRadiusLG: 16,
      headerFontSize: 18,
      colorBorderSecondary: ds.borderSecondary,
    },
    Tag: {
      defaultBg: ds.brandSubtlest,
      defaultColor: ds.textSecondary,
      borderRadiusSM: 999, // element/full (pill)
    },
    Pagination: {
      itemActiveBg: ds.brandSubtlest,
    },
    Dropdown: {
      paddingBlock: 8,
      controlItemBgHover: ds.brandSubtlest,
    },
    Layout: {
      bodyBg: ds.pageBg,
      headerBg: ds.pageBg,
      siderBg: ds.cardBg,
    },
    Typography: {
      titleMarginBottom: 0,
      titleMarginTop: 0,
    },
  },
};
